import { useEffect, useMemo, useState } from "react";
import { Warning, PencilSimple, FloppyDisk, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { alertMeta, ALERT_TONE_CLASSES, CUSTOMER_ALERT_META } from "@/lib/customerAlerts";
import { useAuth } from "@/lib/auth";

/**
 * CustomerAlertsPanel — V1.1.1 OP-004 · Customer Alerts editor.
 *
 * Two display modes:
 *   • Read-only banner (default) — shown at the top of Customer 360.
 *   • Edit mode — Administrator/Office Staff pick flags + type an
 *     operational note (max 500 chars).
 *
 * Change is atomic: PATCH /customers/{id}/alerts replaces the entire
 * flags array and the note. No partial updates — that keeps the audit
 * trail simple.
 */
export default function CustomerAlertsPanel({ customer, onCustomerChange }) {
  const { user } = useAuth();
  const canEdit = user && ["Administrator", "Office Staff"].includes(user.role);
  const [editing, setEditing] = useState(false);
  const [flags, setFlags] = useState(customer.alert_flags || []);
  const [note, setNote] = useState(customer.alert_note || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFlags(customer.alert_flags || []);
    setNote(customer.alert_note || "");
  }, [customer.id, customer.alert_flags, customer.alert_note]);

  const flagList = useMemo(() => Object.keys(CUSTOMER_ALERT_META), []);
  const hasAlerts = (customer.alert_flags || []).length > 0 || (customer.alert_note || "").length > 0;

  const toggleFlag = (id) => {
    setFlags((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/customers/${customer.id}/alerts`, {
        alert_flags: flags,
        alert_note: note,
      });
      onCustomerChange?.(r.data);
      setEditing(false);
      toast.success("Customer alerts updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save alerts");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setFlags(customer.alert_flags || []);
    setNote(customer.alert_note || "");
    setEditing(false);
  };

  if (!hasAlerts && !editing && !canEdit) return null;

  if (!editing) {
    return (
      <div
        data-testid="customer-alerts-panel"
        data-empty={hasAlerts ? "false" : "true"}
        className={`rounded-md border p-3 ${
          hasAlerts
            ? "border-amber-200 bg-amber-50"
            : "border-dashed border-slate-300 bg-slate-50"
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider">
            <Warning size={12} weight="fill" className={hasAlerts ? "text-amber-700" : "text-slate-400"} />
            <span className={hasAlerts ? "text-amber-800" : "text-slate-500"}>
              Operational alerts
            </span>
          </div>
          {canEdit && (
            <button
              data-testid="customer-alerts-edit"
              onClick={() => setEditing(true)}
              className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
            >
              <PencilSimple size={10} weight="bold" className="mr-1 inline" />
              {hasAlerts ? "Edit" : "Add"}
            </button>
          )}
        </div>
        {hasAlerts ? (
          <>
            {(customer.alert_flags || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {customer.alert_flags.map((f) => {
                  const meta = alertMeta(f);
                  return (
                    <span
                      key={f}
                      data-testid={`customer-alert-chip-${f}`}
                      title={meta.description}
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${ALERT_TONE_CLASSES[meta.tone] || ALERT_TONE_CLASSES.slate}`}
                    >
                      {meta.label}
                    </span>
                  );
                })}
              </div>
            )}
            {customer.alert_note && (
              <p
                data-testid="customer-alert-note"
                className="mt-2 whitespace-pre-line text-sm text-amber-900"
              >
                {customer.alert_note}
              </p>
            )}
          </>
        ) : (
          <p className="mt-1 text-xs text-slate-500">
            No operational alerts on file. Add flags like <em>Credit Hold</em>,{" "}
            <em>Keep Full</em>, or a free-text banner for the delivery driver.
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="customer-alerts-editor"
      className="rounded-md border border-slate-300 bg-white p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-900">Edit operational alerts</div>
        <button
          onClick={cancel}
          className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
          aria-label="Close"
          data-testid="customer-alerts-cancel"
        >
          <X size={12} weight="bold" />
        </button>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
        {flagList.map((f) => {
          const meta = alertMeta(f);
          const active = flags.includes(f);
          return (
            <button
              key={f}
              type="button"
              data-testid={`customer-alert-flag-${f}`}
              data-active={active ? "true" : "false"}
              onClick={() => toggleFlag(f)}
              className={`flex items-start gap-2 rounded-md border p-2 text-left text-xs transition ${
                active
                  ? ALERT_TONE_CLASSES[meta.tone] || ALERT_TONE_CLASSES.slate
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              <span className={`mt-0.5 inline-flex h-3 w-3 flex-shrink-0 items-center justify-center rounded border ${active ? "border-current bg-current" : "border-slate-300"}`}>
                {active && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
              </span>
              <span className="min-w-0">
                <span className="block font-semibold">{meta.label}</span>
                <span className="block text-[10px] opacity-75">{meta.description}</span>
              </span>
            </button>
          );
        })}
      </div>

      <label className="mt-2 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        Operational note (500 chars)
      </label>
      <textarea
        data-testid="customer-alerts-note-input"
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 500))}
        rows={3}
        placeholder="e.g. Approach from the concession road, driveway is soft after rain."
        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-brand focus:outline-none"
      />
      <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
        <span>{note.length} / 500</span>
      </div>

      <div className="mt-2 flex justify-end gap-2">
        <button
          data-testid="customer-alerts-cancel-btn"
          onClick={cancel}
          disabled={saving}
          className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          data-testid="customer-alerts-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1 text-sm font-semibold text-brand-foreground hover:bg-brand-hover disabled:opacity-50"
        >
          <FloppyDisk size={12} weight="bold" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
